import { Text, Textarea, VStack } from "@chakra-ui/react";
import { useCallback } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { ConfirmationDialog } from "@/common/components/ConfirmationDialog";
import { FormRootError } from "@/common/components/Form/FormRootError";
import { ManagedFormControl } from "@/common/components/Form/ManagedFormControl";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useFormMutationSubmitHandler } from "@/common/hooks/useApiForm";
import { useUpdateUserFlagAccessMutation } from "@/user/data/useUpdateUserFlagAccessMutation";
import { EntityFlagDTO, UpdateEntityFlagsBody } from "@reflag/shared/flagAPI";
import { UserDTO } from "@reflag/shared/userAPI";

type UserFlagToggleDialogProps = {
  user: Pick<UserDTO, "id" | "name">;
  flag: Pick<EntityFlagDTO, "key" | "name" | "specificTargetValue">;
  isOpen: boolean;
  onClose: () => void;
};

export const UserFlagToggleDialog = ({
  user,
  flag,
  isOpen,
  onClose,
}: UserFlagToggleDialogProps) => {
  const { env } = useEnvironment();
  const isExplicitlyTargeted = flag.specificTargetValue === true;
  const userName = user.name || user.id;

  const placeholder = useCallback(
    (tense: "past" | "future" = "past", lowerCase = false) => {
      let access = !isExplicitlyTargeted
        ? tense === "past"
          ? "Granted"
          : "Grant"
        : tense === "past"
          ? "Revoked"
          : "Revoke";
      access = lowerCase ? access.toLowerCase() : access;
      return `${access} user ${userName} access to flag ${flag.name} in ${env.name}.`;
    },
    [env.name, flag.name, isExplicitlyTargeted, userName],
  );

  const updateUserFlagAccessMutation = useUpdateUserFlagAccessMutation(user.id);

  const form = useForm({
    defaultValues: {
      changeDescription: "",
    },
  });

  const onConfirmClick = useFormMutationSubmitHandler(
    form,
    updateUserFlagAccessMutation,
    () => {
      form.resetField("changeDescription");
      onClose?.();
    },
    {
      prepareVariables({ changeDescription }) {
        return {
          updates: [
            {
              flagKey: flag.key,
              specificTargetValue: isExplicitlyTargeted ? null : true,
            },
          ],
          changeDescription: changeDescription || placeholder("past"),
        } satisfies UpdateEntityFlagsBody;
      },
      successToast: placeholder("past"),
      errorToast: `Failed to ${placeholder("future", true)}`,
    },
  );

  return (
    <FormProvider {...form}>
      <ConfirmationDialog
        _contentProps={{ maxW: "xl" }}
        description={
          <VStack as="form" justify="flex-start" spacing={4} w="full">
            <ManagedFormControl
              name="changeDescription"
              render={({ field }) => (
                <Textarea placeholder={placeholder("past")} {...field} />
              )}
            />
            <FormRootError />
          </VStack>
        }
        isLoading={updateUserFlagAccessMutation.isPending}
        isOpen={isOpen}
        title={
          <Text whiteSpace="nowrap">
            {!isExplicitlyTargeted ? "Grant" : "Revoke"} flag access
          </Text>
        }
        onCancel={() => {
          form.resetField("changeDescription");
          onClose?.();
        }}
        onClose={onClose}
        onConfirm={onConfirmClick}
      />
    </FormProvider>
  );
};
